import { useEffect, useState, useCallback } from "react";
import api, { errMsg } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Empty } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";

const EMPTY = { name: "", position: "", email: "", phone: "", specialty: "", active: true,
                emergency_contact_name: "", emergency_contact_phone: "", emergency_contact_relation: "" };

export default function MntTechnicians() {
  const { t } = useI18n();
  const { can } = useAuth();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const editable = can("admin", "maintenance", "warehouse");

  const load = useCallback(() => {
    api.get("/mnt/technicians").then((r) => setRows(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      if (editId) await api.put(`/mnt/technicians/${editId}`, form);
      else await api.post("/mnt/technicians", form);
      toast.success(t("common.saved"));
      setOpen(false); setForm(EMPTY); setEditId(null); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("¿Eliminar mecánico?")) return;
    try { await api.delete(`/mnt/technicians/${id}`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="mnt-technicians-page">
      <PageHeader title={t("mnt.tech.title")} subtitle={t("mnt.tech.subtitle")}>
        {editable && (
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setForm(EMPTY); setEditId(null); } }}>
            <DialogTrigger asChild>
              <Button size="sm" className="rounded-none text-xs font-bold" data-testid="tech-new-button">
                <Plus size={13} className="mr-1" /> {t("mnt.tech.new")}
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="uppercase font-black">{editId ? t("common.edit") : t("mnt.tech.new")}</DialogTitle>
                <DialogDescription className="text-xs">{t("mnt.tech.subtitle")}</DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2 space-y-1">
                  <Label className="text-[10px] font-bold uppercase tracking-widest">{t("mnt.tech.name")}</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="rounded-none h-8 text-xs" data-testid="tech-form-name" />
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] font-bold uppercase tracking-widest">{t("mnt.tech.position")}</Label>
                  <Input value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })}
                    className="rounded-none h-8 text-xs" placeholder="Mecánico A" data-testid="tech-form-position" />
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] font-bold uppercase tracking-widest">{t("mnt.tech.specialty")}</Label>
                  <Input value={form.specialty} onChange={(e) => setForm({ ...form, specialty: e.target.value })}
                    className="rounded-none h-8 text-xs" placeholder="Motor · Hidráulico…" data-testid="tech-form-specialty" />
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] font-bold uppercase tracking-widest">{t("mnt.tech.email")}</Label>
                  <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="rounded-none h-8 text-xs" data-testid="tech-form-email" />
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] font-bold uppercase tracking-widest">{t("mnt.tech.phone")}</Label>
                  <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="rounded-none h-8 text-xs" data-testid="tech-form-phone" />
                </div>
                <div className="col-span-2 flex items-center gap-2 pt-2">
                  <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="tech-form-active" />
                  <Label className="text-[10px] font-bold uppercase tracking-widest">{t("common.active")}</Label>
                </div>
                <div className="col-span-2 border-t pt-3 mt-1">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-red-700 mb-2">Contacto de emergencia</div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-[10px] font-bold uppercase tracking-widest">Nombre</Label>
                      <Input value={form.emergency_contact_name} onChange={(e) => setForm({ ...form, emergency_contact_name: e.target.value })}
                        className="rounded-none h-8 text-xs" data-testid="tech-form-emg-name" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[10px] font-bold uppercase tracking-widest">Teléfono</Label>
                      <Input value={form.emergency_contact_phone} onChange={(e) => setForm({ ...form, emergency_contact_phone: e.target.value })}
                        className="rounded-none h-8 text-xs" data-testid="tech-form-emg-phone" />
                    </div>
                    <div className="col-span-2 space-y-1">
                      <Label className="text-[10px] font-bold uppercase tracking-widest">Parentesco / Relación</Label>
                      <Input value={form.emergency_contact_relation} onChange={(e) => setForm({ ...form, emergency_contact_relation: e.target.value })}
                        className="rounded-none h-8 text-xs" placeholder="Esposa / Padre / Hermano…" data-testid="tech-form-emg-rel" />
                    </div>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={save} disabled={!form.name}
                  className="rounded-none font-bold text-xs" data-testid="tech-save-button">{t("common.save")}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </PageHeader>

      {rows.length === 0 ? <Empty text={t("common.noData")} /> : (
        <div className="tbl-wrap" data-testid="tech-table">
          <table className="tbl">
            <thead><tr>
              <th>{t("mnt.tech.name")}</th><th>{t("mnt.tech.position")}</th><th>{t("mnt.tech.specialty")}</th>
              <th>{t("mnt.tech.email")}</th><th>{t("mnt.tech.phone")}</th><th>{t("common.active")}</th><th></th>
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-testid={`tech-row-${r.id}`}>
                  <td className="font-bold">{r.name}</td>
                  <td className="text-[11px] text-muted-foreground">{r.position || "—"}</td>
                  <td className="text-[11px] text-muted-foreground">{r.specialty || "—"}</td>
                  <td className="text-[11px] text-muted-foreground">{r.email || "—"}</td>
                  <td className="text-[11px] text-muted-foreground">{r.phone || "—"}</td>
                  <td>{r.active ? <span className="text-[10px] font-bold text-emerald-600">SÍ</span> : <span className="text-[10px] text-muted-foreground">NO</span>}</td>
                  <td>
                    {editable && (
                      <div className="flex gap-2">
                        <button className="text-muted-foreground hover:text-primary" data-testid={`tech-edit-${r.id}`}
                          onClick={() => { setForm({
                            name: r.name, position: r.position || "", email: r.email || "",
                            phone: r.phone || "", specialty: r.specialty || "", active: r.active,
                            emergency_contact_name: r.emergency_contact_name || "",
                            emergency_contact_phone: r.emergency_contact_phone || "",
                            emergency_contact_relation: r.emergency_contact_relation || "",
                          }); setEditId(r.id); setOpen(true); }}>
                          <Pencil size={12} />
                        </button>
                        {can("admin", "maintenance") && (
                          <button className="text-muted-foreground hover:text-destructive" data-testid={`tech-del-${r.id}`}
                            onClick={() => del(r.id)}>
                            <Trash2 size={12} />
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
